import { useState } from 'react';
import { useScrollAnimation, useStaggerAnimation } from '@/hooks/useScrollAnimation';

const features = [
  {
    icon: 'ri-team-line',
    color: 'bg-green-100 text-green-600',
    title: '家庭群组',
    titleEn: 'Family Groups',
    desc: '加入已婚家庭群组，分享育儿经验，互相支持，共同成长。',
    count: '8,200+ 家庭',
  },
  {
    icon: 'ri-calendar-event-line',
    color: 'bg-blue-100 text-blue-600',
    title: '社区活动',
    titleEn: 'Community Events',
    desc: '线上线下活动，相亲联谊、家庭聚会、亲子活动，丰富多彩。',
    count: '每月 50+ 活动',
  },
  {
    icon: 'ri-discuss-line',
    color: 'bg-violet-100 text-violet-600',
    title: '讨论论坛',
    titleEn: 'Discussion Forums',
    desc: '分享恋爱心得、婚姻感悟、育儿经验，与大家庭成员深度交流。',
    count: '日均 2,000+ 帖子',
  },
  {
    icon: 'ri-image-line',
    color: 'bg-orange-100 text-orange-600',
    title: '美好记忆',
    titleEn: 'Photo Memories',
    desc: '记录爱情故事、婚礼瞬间、家庭成长，留住每一个珍贵时刻。',
    count: '50万+ 照片',
  },
];

const familyPhotos = [
  'https://public.readdy.ai/ai/img_res/47e1bbef90ea639e93240d738883194b.jpg',
  'https://readdy.ai/api/search-image?query=Chinese%20couple%20wedding%20day%20portrait%2C%20traditional%20and%20modern%20blend%2C%20warm%20golden%20lighting%2C%20romantic%20atmosphere%2C%20high%20quality%20photography&width=300&height=300&seq=fam002&orientation=squarish',
  'https://readdy.ai/api/search-image?query=Chinese%20family%20outdoor%20picnic%20park%20scene%2C%20parents%20and%20children%20playing%2C%20sunny%20day%2C%20warm%20tones%2C%20high%20quality%20photography&width=300&height=300&seq=fam003&orientation=squarish',
  'https://readdy.ai/api/search-image?query=Chinese%20couple%20cooking%20together%20in%20kitchen%2C%20warm%20home%20atmosphere%2C%20happy%20expressions%2C%20soft%20lighting%2C%20high%20quality%20photography&width=300&height=300&seq=fam004&orientation=squarish',
  'https://readdy.ai/api/search-image?query=Chinese%20grandparents%20with%20grandchildren%20in%20garden%2C%20multigenerational%20family%2C%20warm%20sunlight%2C%20joyful%20scene%2C%20high%20quality%20photography&width=300&height=300&seq=fam005&orientation=squarish',
  'https://readdy.ai/api/search-image?query=Chinese%20family%20celebrating%20new%20year%20together%2C%20festive%20decorations%2C%20warm%20red%20and%20gold%20tones%2C%20happy%20gathering%2C%20high%20quality%20photography&width=300&height=300&seq=fam006&orientation=squarish',
];

export default function BigFamily() {
  const [activeFeature, setActiveFeature] = useState(0);
  const { ref: headerRef, isVisible: headerVisible } = useScrollAnimation();
  const { ref: cardsRef, visibleItems: cardItems } = useStaggerAnimation(features.length);
  const { ref: galleryRef, isVisible: galleryVisible } = useScrollAnimation({ threshold: 0.1 });
  const { ref: ctaRef, isVisible: ctaVisible } = useScrollAnimation({ threshold: 0.2 });

  return (
    <section id="community" className="py-20 bg-gradient-to-br from-orange-50 via-rose-50 to-amber-50">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div
          ref={headerRef as React.RefObject<HTMLDivElement>}
          className="text-center mb-12 transition-all duration-700"
          style={{ opacity: headerVisible ? 1 : 0, transform: headerVisible ? 'translateY(0)' : 'translateY(30px)' }}
        >
          <div className="inline-flex items-center gap-2 bg-white border border-orange-200 rounded-full px-4 py-1.5 text-xs font-semibold text-orange-600 mb-4">
            <i className="ri-home-heart-line"></i> 大家庭社区 · Big Family Community
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-3" style={{ fontFamily: "'Noto Serif SC', serif" }}>
            加入我们的大家庭
          </h2>
          <p className="text-gray-400 text-sm max-w-lg mx-auto">
            连接已婚夫妇，分享育儿经验，建立持久的友谊，共同构建温暖的大家庭社区
          </p>
        </div>

        {/* Features Grid */}
        <div
          ref={cardsRef as React.RefObject<HTMLDivElement>}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-14"
        >
          {features.map((f, i) => (
            <div
              key={i}
              onClick={() => setActiveFeature(i)}
              className={`p-5 rounded-2xl cursor-pointer transition-all duration-700 ${activeFeature === i ? 'bg-white shadow-md' : 'bg-white/60 hover:bg-white'}`}
              style={{ opacity: cardItems[i] ? 1 : 0, transform: cardItems[i] ? 'translateY(0)' : 'translateY(40px)', transitionDelay: `${i * 100}ms` }}
            >
              <div className={`w-12 h-12 flex items-center justify-center rounded-xl mb-4 ${f.color}`}>
                <i className={`${f.icon} text-xl`}></i>
              </div>
              <h4 className="text-base font-bold text-gray-800 mb-1">{f.title}</h4>
              <p className="text-xs text-gray-400 mb-3 leading-relaxed">{f.desc}</p>
              <div className="text-xs font-semibold text-orange-500">{f.count}</div>
              <a href="#" className="text-xs text-red-500 hover:text-red-600 mt-2 inline-block cursor-pointer">
                了解更多 <i className="ri-arrow-right-line"></i>
              </a>
            </div>
          ))}
        </div>

        {/* Photo Gallery */}
        <div
          ref={galleryRef as React.RefObject<HTMLDivElement>}
          className="mb-12 transition-all duration-700"
          style={{ opacity: galleryVisible ? 1 : 0, transform: galleryVisible ? 'translateY(0)' : 'translateY(30px)' }}
        >
          <h3 className="text-xl font-bold text-gray-800 text-center mb-6" style={{ fontFamily: "'Noto Serif SC', serif" }}>
            大家庭的美好瞬间
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {familyPhotos.map((photo, i) => (
              <div key={i} className="aspect-square rounded-xl overflow-hidden cursor-pointer group">
                <img
                  src={photo}
                  alt={`family moment ${i + 1}`}
                  className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                />
              </div>
            ))}
          </div>
        </div>

        {/* CTA Banner */}
        <div
          ref={ctaRef as React.RefObject<HTMLDivElement>}
          className="bg-gradient-to-r from-red-600 to-rose-600 rounded-2xl p-8 md:p-12 text-center text-white transition-all duration-700"
          style={{ opacity: ctaVisible ? 1 : 0, transform: ctaVisible ? 'scale(1)' : 'scale(0.97)' }}
        >
          <h3 className="text-2xl md:text-3xl font-bold mb-3" style={{ fontFamily: "'Noto Serif SC', serif" }}>
            立即加入富缘佳旺大家庭
          </h3>
          <p className="text-white/80 text-sm mb-6 max-w-md mx-auto">
            600万+ 认证会员正在等待与您相遇，开启您的幸福缘分之旅
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="px-8 py-3 bg-white text-red-600 rounded-full font-semibold text-sm hover:bg-gray-50 transition-all cursor-pointer whitespace-nowrap">
              免费注册 <i className="ri-arrow-right-line ml-1"></i>
            </button>
            <button className="px-8 py-3 border-2 border-white text-white rounded-full font-semibold text-sm hover:bg-white/10 transition-all cursor-pointer whitespace-nowrap">
              <i className="ri-play-circle-line mr-1"></i> 了解更多
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
